import { type CardDispute, type CardDisputeEvent, CardDisputeResponseAction } from '@column/api';
import { formatNumber } from '@column/column-ui-kit';

import { TimelineEventStatus, type TimelineEventsEntry } from '~/components/TimelineEvents';

import { CARD_DISPUTE_STAGE_LABELS, type CardDisputeStage, getDisputeResolutionLabel } from './labels';

const STAGE_EVENT_STATUSES: Partial<Record<CardDisputeStage, TimelineEventStatus>> = {
  disputed: TimelineEventStatus.Success,
  prearbitration: TimelineEventStatus.Warning,
  arbitration: TimelineEventStatus.Hold,
  dispute_won: TimelineEventStatus.Success,
};

const STAGE_TIMELINE_CONTENT: Partial<Record<CardDisputeStage, string>> = {
  initiated: 'The dispute was created.',
  disputed: 'The dispute was filed with the Card Network.',
  merchant_responded: 'The merchant responded to the dispute. Review their evidence and respond.',
  merchant_response_recalled: 'The merchant recalled its response.',
  prearbitration: 'The dispute moved to pre-arbitration.',
  prearbitration_responded: 'A pre-arbitration response was submitted.',
  arbitration: 'Arbitration — status not yet confirmed.',
  dispute_won: 'The dispute was resolved in the cardholder’s favor.',
  dispute_close: 'This dispute was closed.',
};

const TERMINAL_STAGES: Partial<Record<CardDisputeStage, true>> = {
  dispute_won: true,
  dispute_close: true,
};

// The response intent recorded on an event is the only record of that user action, so
// response-action events must never be collapsed into their stage transition. Unknown actions
// (e.g. the internal recall intent) fall back to a neutral formatted label.
const RESPONSE_ACTION_LABELS: Record<CardDisputeResponseAction, string> = {
  [CardDisputeResponseAction.ACCEPT]: 'Response recorded — accepted',
  [CardDisputeResponseAction.ACCEPT_PARTIAL]: 'Response recorded — partially accepted',
  [CardDisputeResponseAction.DECLINE]: 'Response recorded — declined',
  [CardDisputeResponseAction.ESCALATE]: 'Response recorded — escalated',
};

const isEvidenceEvent = (event: CardDisputeEvent): boolean =>
  Boolean(event.evidence_document_id || event.evidence_narrative);

const isResponseActionEvent = (event: CardDisputeEvent): boolean => Boolean(event.response_action);

// Pure stage transitions can be collapsed; anything carrying a response intent or evidence is
// consequential on its own.
const isCollapsibleStageTransition = (event: CardDisputeEvent): boolean =>
  !isEvidenceEvent(event) && !isResponseActionEvent(event);

export const dedupeConsecutiveStageEvents = (events: CardDisputeEvent[]): CardDisputeEvent[] => {
  const deduped: CardDisputeEvent[] = [];

  for (const event of events) {
    const previous = deduped[deduped.length - 1];
    if (
      previous &&
      isCollapsibleStageTransition(event) &&
      isCollapsibleStageTransition(previous) &&
      previous.stage === event.stage
    ) {
      continue;
    }
    deduped.push(event);
  }

  return deduped;
};

export const sortDisputeEventsOldestFirst = (events: CardDisputeEvent[]): CardDisputeEvent[] =>
  [...events].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

export const getVisitedStages = (dispute: Pick<CardDispute, 'events' | 'stage'>): CardDisputeStage[] => {
  const stages = dedupeConsecutiveStageEvents(sortDisputeEventsOldestFirst(dispute.events ?? [])).map(
    (event) => event.stage
  );

  if (dispute.stage && !stages.includes(dispute.stage)) {
    stages.push(dispute.stage);
  }
  return stages;
};

const buildEventLabel = (event: CardDisputeEvent, dispute: CardDispute): string => {
  if (isResponseActionEvent(event)) {
    return (
      (RESPONSE_ACTION_LABELS as Record<string, string>)[event.response_action!] ??
      `Response recorded — ${event.response_action}`
    );
  }
  if (isEvidenceEvent(event)) return 'Evidence added';

  const isCurrentTerminalStage = event.stage === dispute.stage && TERMINAL_STAGES[event.stage];
  if (isCurrentTerminalStage) {
    // resolution_reason can be null on terminal events — fall back to a generic label.
    return getDisputeResolutionLabel(dispute.resolution_reason, event.stage);
  }

  return CARD_DISPUTE_STAGE_LABELS[event.stage];
};

const buildEventContent = (event: CardDisputeEvent, dispute: CardDispute): string | undefined => {
  const parts: string[] = [];
  if (isResponseActionEvent(event)) {
    // accept_partial records the accepted-liability amount in the dispute's currency.
    if (event.response_amount) {
      parts.push(`Accepted liability for ${formatNumber(event.response_amount, dispute.currency)}.`);
    }
    // Responses to a compelling-evidence pre-arbitration record the issuer's certifications.
    if (typeof event.compelling_evidence_reviewed_with_cardholder === 'boolean') {
      parts.push(
        event.compelling_evidence_reviewed_with_cardholder
          ? 'Certified the compelling evidence was reviewed with the cardholder, who continues to dispute.'
          : 'Did not certify that the compelling evidence was reviewed with the cardholder.'
      );
    }
    if (typeof event.cardholder_name_and_address_mismatch === 'boolean') {
      parts.push(
        event.cardholder_name_and_address_mismatch
          ? 'Certified the cardholder name and address in the compelling evidence differ from your records.'
          : 'Certified the cardholder name and address in the compelling evidence match your records.'
      );
    }
  }
  if (isEvidenceEvent(event) && event.evidence_narrative) {
    parts.push(event.evidence_narrative);
  }
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (isResponseActionEvent(event) || isEvidenceEvent(event)) {
    return undefined;
  }

  if (TERMINAL_STAGES[event.stage] && event.stage === dispute.stage && dispute.resolution_reason) {
    return undefined;
  }

  return STAGE_TIMELINE_CONTENT[event.stage];
};

export const buildCardDisputeTimelineEvents = (dispute: CardDispute): TimelineEventsEntry[] => {
  const sortedEvents = sortDisputeEventsOldestFirst(dispute.events ?? []);

  return dedupeConsecutiveStageEvents(sortedEvents).map((event) => ({
    label: buildEventLabel(event, dispute),
    date: event.created_at ? new Date(event.created_at) : undefined,
    status: isEvidenceEvent(event)
      ? TimelineEventStatus.Success
      : isResponseActionEvent(event)
        ? TimelineEventStatus.Success
        : STAGE_EVENT_STATUSES[event.stage],
    content: buildEventContent(event, dispute),
  }));
};
